import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { emailTemplatesApi, getErrorMessage } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, 
  Mail, 
  Bell, 
  CheckCircle2, 
  Shield,
  FileText,
  RefreshCw,
  Trash2,
  Edit2,
  Star,
  Eye,
  Code
} from 'lucide-react';
import { format } from 'date-fns';

// Template type configuration
const TEMPLATE_TYPES = {
  notification: { 
    label: 'Document Notification', 
    description: 'Sent when documents are first assigned',
    icon: Mail, 
    color: 'text-blue-600 bg-blue-50' 
  },
  reminder: { 
    label: 'Reminder', 
    description: 'Follow-up for pending documents',
    icon: Bell, 
    color: 'text-amber-600 bg-amber-50' 
  },
  completion: { 
    label: 'Completion Confirmation', 
    description: 'Thank you after submission',
    icon: CheckCircle2, 
    color: 'text-green-600 bg-green-50' 
  },
  approval_needed: { 
    label: 'Approval Notification', 
    description: 'Alert admin when approval needed',
    icon: Shield, 
    color: 'text-purple-600 bg-purple-50' 
  },
  custom: { 
    label: 'Custom', 
    description: 'Custom email template',
    icon: FileText, 
    color: 'text-slate-600 bg-slate-50' 
  },
};

// Available template variables
const TEMPLATE_VARIABLES = [
  { name: '{{name}}', description: 'Recipient\'s full name' },
  { name: '{{first_name}}', description: 'Recipient\'s first name' },
  { name: '{{email}}', description: 'Recipient\'s email' },
  { name: '{{document_name}}', description: 'Name of the document' },
  { name: '{{document_link}}', description: 'Link to complete the document' },
  { name: '{{pending_count}}', description: 'Number of pending documents' },
  { name: '{{company_name}}', description: 'Company name (for contacts)' },
  { name: '{{expiry_date}}', description: 'Document expiration date' },
  { name: '{{sender_name}}', description: 'Name of the sender' },
];

// Default template content
const DEFAULT_TEMPLATES = {
  notification: {
    subject: 'Action Required: Please complete your {{document_name}}',
    body: `<p>Hi {{name}},</p>
<p>You have been assigned a new document to complete: <strong>{{document_name}}</strong>.</p>
<p>Please click the link below to review and complete the document:</p>
<p><a href="{{document_link}}">Complete Document</a></p>
<p>This link will expire on {{expiry_date}}.</p>
<p>If you have any questions, please don't hesitate to reach out.</p>
<p>Best regards,<br>{{sender_name}}</p>`
  },
  reminder: {
    subject: 'Reminder: {{pending_count}} document(s) pending completion',
    body: `<p>Hi {{name}},</p>
<p>This is a friendly reminder that you have <strong>{{pending_count}}</strong> document(s) pending completion.</p>
<p>Please complete them at your earliest convenience to avoid any delays.</p>
<p><a href="{{document_link}}">Complete Documents</a></p>
<p>Thank you for your prompt attention.</p>
<p>Best regards,<br>{{sender_name}}</p>`
  },
  completion: {
    subject: 'Thank you! Your {{document_name}} has been received',
    body: `<p>Hi {{name}},</p>
<p>Thank you for completing your <strong>{{document_name}}</strong>.</p>
<p>We have received your submission and it is now being reviewed.</p>
<p>You will be notified once it has been approved.</p>
<p>Best regards,<br>{{sender_name}}</p>`
  },
  approval_needed: {
    subject: 'Approval Required: {{name}} submitted {{document_name}}',
    body: `<p>A document requires your approval:</p>
<ul>
<li><strong>Recipient:</strong> {{name}} ({{email}})</li>
<li><strong>Document:</strong> {{document_name}}</li>
</ul>
<p>Please review and approve the document in the admin dashboard.</p>`
  }
};

// Template Editor component
const TemplateEditor = ({ template, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: template?.name || '',
    template_type: template?.template_type || 'notification',
    subject: template?.subject || '',
    body: template?.body || '',
    is_default: template?.is_default || false,
  });
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    if (!template && formData.template_type && !formData.subject && !formData.body) {
      const defaults = DEFAULT_TEMPLATES[formData.template_type];
      if (defaults) {
        setFormData(prev => ({
          ...prev,
          subject: defaults.subject,
          body: defaults.body,
        }));
      }
    }
  }, [formData.template_type]);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    if (!formData.subject.trim()) {
      toast.error('Please enter a subject line');
      return;
    }
    if (!formData.body.trim()) {
      toast.error('Please enter email body content');
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  const insertVariable = (variable) => {
    setFormData(prev => ({
      ...prev,
      body: prev.body + variable
    }));
  };

  // Preview with sample data
  const getPreviewContent = (content) => {
    return content
      .replace(/\{\{name\}\}/g, 'John Smith')
      .replace(/\{\{first_name\}\}/g, 'John')
      .replace(/\{\{email\}\}/g, 'john.smith@example.com')
      .replace(/\{\{document_name\}\}/g, 'Employment Contract')
      .replace(/\{\{document_link\}\}/g, '#')
      .replace(/\{\{pending_count\}\}/g, '2')
      .replace(/\{\{company_name\}\}/g, 'Acme Corp')
      .replace(/\{\{expiry_date\}\}/g, 'March 30, 2026')
      .replace(/\{\{sender_name\}\}/g, 'HR Team');
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Template Name</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Welcome Email"
            data-testid="template-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Template Type</Label>
          <Select 
            value={formData.template_type} 
            onValueChange={(v) => setFormData({ ...formData, template_type: v })}
            disabled={!!template}
          >
            <SelectTrigger data-testid="template-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(TEMPLATE_TYPES).map(([value, { label }]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Subject Line</Label>
        <Input
          value={formData.subject}
          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
          placeholder="Email subject..."
          data-testid="template-subject-input"
        />
      </div>

      {/* Variable Chips */}
      <div className="space-y-2">
        <Label>Insert Variables</Label>
        <div className="flex flex-wrap gap-2">
          {TEMPLATE_VARIABLES.map((v) => (
            <button
              key={v.name}
              type="button"
              onClick={() => insertVariable(v.name)}
              className="px-2 py-1 text-xs font-mono bg-slate-100 hover:bg-slate-200 rounded border border-slate-300 transition-colors"
              title={v.description}
            >
              {v.name}
            </button>
          ))}
        </div>
      </div>

      {/* Body Editor with Preview Toggle */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Email Body (HTML)</Label>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant={previewMode ? "outline" : "default"}
              size="sm"
              onClick={() => setPreviewMode(false)}
            >
              <Code className="w-4 h-4 mr-1" />
              Edit
            </Button>
            <Button
              type="button"
              variant={previewMode ? "default" : "outline"}
              size="sm"
              onClick={() => setPreviewMode(true)}
            >
              <Eye className="w-4 h-4 mr-1" />
              Preview
            </Button>
          </div>
        </div>
        
        {previewMode ? (
          <div className="border rounded-lg p-4 min-h-[200px] bg-white">
            <div className="border-b pb-2 mb-4">
              <p className="text-sm text-muted-foreground">Subject:</p>
              <p className="font-medium">{getPreviewContent(formData.subject)}</p>
            </div>
            <div 
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(getPreviewContent(formData.body)) }}
            />
          </div>
        ) : (
          <Textarea
            value={formData.body}
            onChange={(e) => setFormData({ ...formData, body: e.target.value })}
            placeholder="<p>Email content...</p>"
            className="font-mono text-sm min-h-[200px]"
            data-testid="template-body-input"
          />
        )}
      </div>

      <div className="flex items-center gap-2">
        <Checkbox
          id="is-default"
          checked={formData.is_default}
          onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked })}
          data-testid="template-default-checkbox"
        />
        <Label htmlFor="is-default" className="cursor-pointer">
          Set as default template for {TEMPLATE_TYPES[formData.template_type]?.label}
        </Label>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving} className="btn-primary">
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
          {template ? 'Update Template' : 'Create Template'}
        </Button>
      </div>
    </div>
  );
};

export default function EmailTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await emailTemplatesApi.list();
      setTemplates(response.data || []);
    } catch (error) {
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (formData) => {
    try {
      await emailTemplatesApi.create(formData);
      toast.success('Template created successfully');
      setIsEditorOpen(false);
      loadTemplates();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create template'));
    }
  };

  const handleUpdate = async (formData) => {
    try {
      await emailTemplatesApi.update(editingTemplate.id, formData);
      toast.success('Template updated successfully');
      setIsEditorOpen(false);
      setEditingTemplate(null);
      loadTemplates();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update template'));
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Delete "${template.name}"? This cannot be undone.`)) return;
    
    setDeleting(template.id);
    try {
      await emailTemplatesApi.delete(template.id);
      toast.success('Template deleted');
      loadTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    } finally {
      setDeleting(null);
    }
  };

  const openEditor = (template = null) => {
    setEditingTemplate(template);
    setIsEditorOpen(true);
  };

  const filteredTemplates = activeTab === 'all' 
    ? templates 
    : templates.filter(t => t.template_type === activeTab);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="email-templates-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold">Email Templates</h1>
            <p className="text-muted-foreground">Manage email templates for document notifications and reminders</p>
          </div>
          <Button onClick={() => openEditor(null)} className="btn-primary" data-testid="create-template-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Template
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-muted/50">
            <TabsTrigger value="all">All</TabsTrigger>
            {Object.entries(TEMPLATE_TYPES).map(([value, { label }]) => (
              <TabsTrigger key={value} value={value}>{label}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : filteredTemplates.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredTemplates.map((template) => {
                  const typeConfig = TEMPLATE_TYPES[template.template_type] || TEMPLATE_TYPES.custom;
                  const TypeIcon = typeConfig.icon;
                  
                  return (
                    <Card key={template.id} className="neumorphic hover:shadow-lg transition-all">
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div className={`w-10 h-10 rounded-lg ${typeConfig.color} flex items-center justify-center`}>
                            <TypeIcon className="w-5 h-5" />
                          </div>
                          <div className="flex items-center gap-1">
                            {template.is_default && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full flex items-center gap-1">
                                <Star className="w-3 h-3" />
                                Default
                              </span>
                            )}
                          </div>
                        </div>
                        <CardTitle className="text-lg mt-3">{template.name}</CardTitle>
                        <CardDescription className="text-xs">{typeConfig.label}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                          {template.subject}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">
                            {template.created_at && format(new Date(template.created_at), 'MMM d, yyyy')}
                          </span>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => openEditor(template)}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDelete(template)}
                              disabled={deleting === template.id}
                            >
                              {deleting === template.id ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16">
                <Mail className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium mb-2">No templates yet</h3>
                <p className="text-muted-foreground mb-4">Create your first email template to get started</p>
                <Button onClick={() => openEditor(null)} className="btn-primary">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Template
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Editor Dialog */}
        <Dialog open={isEditorOpen} onOpenChange={setIsEditorOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingTemplate ? 'Edit Email Template' : 'Create Email Template'}
              </DialogTitle>
            </DialogHeader>
            <TemplateEditor
              template={editingTemplate}
              onSave={editingTemplate ? handleUpdate : handleCreate}
              onCancel={() => {
                setIsEditorOpen(false);
                setEditingTemplate(null);
              }}
            />
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
